import React, { useEffect, useRef } from 'react';
import { LoadingAnimation, useFormContextRequired } from 'ui-builder';
import { EventTask, useProjectAssignmentsService } from 'nekst-api';
import { useDataLoaderNew } from 'data-loader';
import { EventLayoutChoice } from './types';
import Layout from './Layout';
import { useProjectTaskDefaultAssigneeHelper } from '../../defaultAssigneeHelper';

function useProjectAssignmentsHelper(onUpdate?: () => void) {
  const projectAssignmentService = useProjectAssignmentsService();

  const formContext = useFormContextRequired<{ projectId?: number }>();

  const projectId = formContext.data?.projectId;

  const didMount = useRef(false);

  useEffect(() => {
    if (didMount.current) {
      onUpdate?.();
    } else if (formContext.data?.projectId) {
      didMount.current = true;
    }
  }, [formContext.data?.projectId]);

  const { data: projectAssignments } = useDataLoaderNew(
    async () => {
      if (projectId) {
        return await projectAssignmentService.getContactAssignments(projectId);
      } else {
        return [];
      }
    },
    {
      dependencies: [projectId],
    }
  );

  return {
    projectAssignments,
  };
}

export function ProjectEventTransactionPartiesInput() {

  const formContext = useFormContextRequired<EventTask>();

  const projectId = formContext.data?.projectId;

  const taskAssignments = formContext.data?.assignments || [];

  const invitations = formContext.data!.inviteRecipients || [];

  const { projectAssignments } = useProjectAssignmentsHelper(() => {
    formContext.onChangeCallback({
      assignments: [],
      inviteRecipients: [],
    });
  });

  const choices: EventLayoutChoice[] = [];

  const findTaskAssignment = (personId: number) => {
    return taskAssignments.find((assignment) => assignment.personId === personId);
  };

  projectAssignments?.forEach((projectAssignment) => {
    const taskAssignment = findTaskAssignment(projectAssignment.personId);

    choices.push({
      id: projectAssignment.personId,
      name: `${projectAssignment.profile.name.fullName}`,
      nameHint: projectAssignment.roles.map((role) => role.roleName).join(', '),
      isAssignable: projectAssignment.isAssignable,
      isAssigned: !!taskAssignment,
      isInvited: !!invitations.find((invitation) => invitation.personId === projectAssignment.personId),
      _assignment: projectAssignment
    });
  });

  const onAssignmentChangedCallback = (personId: number) => {

    const newValue = [
      ...taskAssignments,
    ];

    const index = newValue.findIndex((assignment) => assignment.personId === personId);

    if (index !== -1) {
      newValue.splice(index, 1);
    } else {
      newValue.push({
        personId,
      });
    }

    formContext.onChangeCallback({
      assignments: newValue,
    });
  };

  const onInvitationChangedCallback = (personId: number) => {
    const index = invitations.findIndex((invitation) => invitation.personId === personId);

    const newValue: { personId: number, id?: number }[] = [
      ...invitations,
    ];

    if (index !== -1) {
      newValue.splice(index, 1);
    } else {
      newValue.push({
        personId,
      });
    }

    formContext.onChangeCallback({
      inviteRecipients: newValue,
    });
  };

  useProjectTaskDefaultAssigneeHelper(
    projectAssignments || [],
    taskAssignments,
    (newValue) => {
      formContext.onChangeCallback({
        assignments: newValue,
        inviteRecipients: newValue,
      });
    }
  );

  if (projectId) {
    if (projectAssignments) {
      return (
        <Layout
          choices={choices}
          onAssignmentChangedCallback={onAssignmentChangedCallback}
          onInvitationChangedCallback={onInvitationChangedCallback}
        />
      );
    } else {
      return (<LoadingAnimation />);
    }
  } else {
    return null;
  }
}
